import React, { useState, useEffect } from 'react';
import { X, CheckCircle, AlertTriangle, Clock, BookOpen, Info } from 'lucide-react';
import Button from '../../../components/ui/Button';
import { Checkbox } from '../../../components/ui/Checkbox';
import * as enrollmentService from '../../../services/enrollmentService';

const UnitRegistrationModal = ({ student, academicYearId, semesterId, onClose }) => {
  const [loading, setLoading] = useState(false);
  const [availableUnits, setAvailableUnits] = useState([]);
  const [currentEnrollments, setCurrentEnrollments] = useState([]);
  const [selectedUnits, setSelectedUnits] = useState([]);
  const [validationResults, setValidationResults] = useState({});
  const [semesterEnrollment, setSemesterEnrollment] = useState(null);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      // Get student's program enrollment
      const programEnrollment = await enrollmentService.getStudentProgramEnrollment(student.id);
      
      if (!programEnrollment.success || !programEnrollment.data) {
        alert('Student is not enrolled in any program');
        onClose();
        return;
      }

      const { program_id, level_id } = programEnrollment.data;
      const progression = await enrollmentService.syncStudentModuleProgression(student.profile_id || student.id);
      if (!progression.success) console.warn('Module progression sync was skipped:', progression.error);
      const enrollment = await enrollmentService.getSemesterEnrollment(student.profile_id || student.id, academicYearId, semesterId);
      if (!enrollment || enrollment.status !== 'active') {
        alert('The student must report and have the semester account activated before registering units.');
        onClose();
        return;
      }
      setSemesterEnrollment(enrollment);
      // Prefer the recalculated student module and never let a stale
      // semester-enrollment row move the registration view backwards.
      const effectiveModule = Math.max(
        Number(progression.data?.current_module || 0),
        Number(enrollment.module_number || 0),
        Number(programEnrollment.data.module_number || 1),
        1
      );

      // Load available units and current enrollments
      const [unitsRes, enrollmentsRes] = await Promise.all([
        enrollmentService.getAvailableUnits(program_id, level_id, semesterId, effectiveModule),
        enrollmentService.getStudentEnrollments(student.profile_id || student.id, academicYearId, semesterId)
      ]);

      if (unitsRes.success) {
        setAvailableUnits(unitsRes.data || []);
      }

      if (enrollmentsRes.success) {
        setCurrentEnrollments(enrollmentsRes.data || []);
      }
    } catch (error) {
      console.error('Error loading data:', error);
      alert('Failed to load enrollment data: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  const handleUnitToggle = async (unitId) => {
    if (selectedUnits.includes(unitId)) {
      setSelectedUnits(selectedUnits.filter(id => id !== unitId));
      return;
    }

    // Validate prerequisites and capacity
    setLoading(true);
    try {
      const [prerequisiteCheck, capacityCheck] = await Promise.all([
        enrollmentService.validatePrerequisites(student.id, unitId),
        enrollmentService.checkUnitCapacity(unitId)
      ]);

      setValidationResults(prev => ({
        ...prev,
        [unitId]: {
          prerequisites: prerequisiteCheck.data,
          capacity: capacityCheck.data
        }
      }));

      setSelectedUnits([...selectedUnits, unitId]);
    } catch (error) {
      console.error('Error validating unit:', error);
      alert('Failed to validate unit: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  const handleEnrollment = async () => {
    if (selectedUnits.length === 0) {
      alert('Please select at least one unit');
      return;
    }

    setLoading(true);
    const results = [];

    try {
      for (const unitId of selectedUnits) {
        const result = await enrollmentService.enrollStudentInUnit({
          studentId: student.profile_id || student.id,
          unitId: unitId,
          academicYearId: academicYearId,
          semesterId: semesterId,
          overridePrerequisites: false
        });

        results.push({
          unitId,
          success: result.success,
          message: result.data?.message || result.error
        });
      }

      // Show results
      const successful = results.filter(r => r.success).length;
      const failed = results.filter(r => !r.success).length;

      alert(
        `Enrollment saved.\n` +
        `✓ Successful: ${successful}\n` +
        `✗ Failed: ${failed}\n\n` +
        (failed > 0 ? 'Check console for error details.' : 'If all current-module units are now registered, billing was posted automatically.')
      );

      if (failed > 0) {
        console.log('Enrollment results:', results);
      }

      setSelectedUnits([]);
      await loadData();
    } catch (error) {
      console.error('Error during enrollment:', error);
      alert('Failed to complete enrollment: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  const handleCompleteRegistration = async () => {
    if (!semesterEnrollment?.id || currentEnrollments.filter(item => item.enrollment_status === 'enrolled').length === 0) {
      alert('Register at least one unit before completing registration.');
      return;
    }
    if (!window.confirm('Complete unit registration and bill the student automatically? The active semester charges will be posted immediately.')) return;
    setLoading(true);
    try {
      await enrollmentService.completeUnitRegistration(semesterEnrollment.id);
      alert('Unit registration completed and billed automatically.');
      onClose();
    } catch (error) {
      alert('Could not complete registration: ' + (error?.message || error));
    } finally { setLoading(false); }
  };

  const isUnitEnrolled = (unitId) => {
    return currentEnrollments.some(e => e.unit_id === unitId);
  };

  const getUnitValidationStatus = (unitId) => {
    const validation = validationResults[unitId];
    if (!validation) return null;

    const hasPrerequisites = validation.prerequisites?.is_valid;
    const hasCapacity = validation.capacity?.has_capacity;

    if (!hasPrerequisites) {
      return {
        type: 'error',
        icon: AlertTriangle,
        message: 'Prerequisites not met',
        details: validation.prerequisites?.missing_prerequisites
      };
    }

    if (!hasCapacity) {
      return {
        type: 'warning',
        icon: Clock,
        message: 'No capacity - will be added to waiting list',
        details: validation.capacity
      };
    }

    return {
      type: 'success',
      icon: CheckCircle,
      message: 'Ready to enroll',
      details: validation.capacity
    };
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-hidden">
        {/* Header */}
        <div className="bg-gradient-to-r from-blue-600 to-indigo-600 px-6 py-4 flex items-center justify-between">
          <div>
            <h2 className="text-xl font-bold text-white">Unit Registration</h2>
            <p className="text-blue-100 text-sm mt-1">
              {student?.full_name} ({student?.student_id})
            </p>
          </div>
          <button
            onClick={onClose}
            className="text-white hover:bg-white/20 rounded-full p-2 transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Content */}
        <div className="p-6 overflow-y-auto max-h-[calc(90vh-180px)]">
          {loading && availableUnits.length === 0 ? (
            <div className="flex items-center justify-center py-12">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
              <span className="ml-4 text-gray-600">Loading available units...</span>
            </div>
          ) : (
            <>
              {/* Current Enrollments */}
              {currentEnrollments.length > 0 && (
                <div className="mb-6">
                  <h3 className="text-lg font-semibold text-gray-900 mb-3">
                    Current Enrollments
                  </h3>
                  <div className="bg-blue-50 rounded-lg p-4 space-y-2">
                    {currentEnrollments.map(enrollment => (
                      <div key={enrollment.id} className="flex items-center justify-between">
                        <div className="flex items-center">
                          <BookOpen className="w-4 h-4 text-blue-600 mr-2" />
                          <span className="text-sm font-medium text-gray-900">
                            {enrollment.unit?.unit_code} - {enrollment.unit?.unit_name}
                          </span>
                        </div>
                        <span className={`text-xs px-2 py-1 rounded-full ${
                          enrollment.enrollment_status === 'enrolled' ? 'bg-green-100 text-green-800' :
                          enrollment.enrollment_status === 'waiting' ? 'bg-yellow-100 text-yellow-800' : 'bg-gray-100 text-gray-800'
                        }`}>
                          {enrollment.enrollment_status}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Available Units */}
              <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-3">
                  Available Units for Registration
                </h3>
                <div className="space-y-3">
                  {availableUnits.map(unit => {
                    const isEnrolled = isUnitEnrolled(unit.id);
                    const isSelected = selectedUnits.includes(unit.id);
                    const validation = getUnitValidationStatus(unit.id);

                    return (
                      <div
                        key={unit.id}
                        className={`border rounded-lg p-4 transition-all ${
                          isEnrolled ? 'bg-gray-50 border-gray-300 opacity-60' : isSelected ? 'border-blue-500 bg-blue-50' : 'border-gray-200 hover:border-blue-300'
                        }`}
                      >
                        <div className="flex items-start justify-between">
                          <div className="flex items-start flex-1">
                            <Checkbox
                              checked={isSelected}
                              onChange={() => handleUnitToggle(unit.id)}
                              disabled={isEnrolled || loading}
                              className="mt-1"
                            />
                            <div className="ml-3 flex-1">
                              <div className="flex items-center gap-2">
                                <h4 className="font-semibold text-gray-900">
                                  {unit.unit_code} - {unit.unit_name}
                                </h4>
                                <span className="text-xs px-2 py-1 bg-purple-100 text-purple-800 rounded-full">
                                  {unit.credits} Credits
                                </span>
                              </div>

                              {/* Capacity Info */}
                              <div className="mt-2 flex items-center gap-4 text-sm text-gray-600">
                                <span>
                                  Capacity: {unit.current_enrollment || 0}/{unit.max_capacity || 0}
                                </span>
                                {unit.prerequisites && unit.prerequisites.length > 0 && (
                                  <span className="flex items-center">
                                    <Info className="w-4 h-4 mr-1" />
                                    Has Prerequisites
                                  </span>
                                )}
                              </div>

                              {/* Prerequisites List */}
                              {unit.prerequisites && unit.prerequisites.length > 0 && (
                                <div className="mt-2 text-sm">
                                  <span className="font-medium text-gray-700">Prerequisites:</span>
                                  <ul className="list-disc list-inside ml-2 text-gray-600">
                                    {unit.prerequisites.map((prereq, idx) => (
                                      <li key={idx}>
                                        {prereq.prerequisite_unit?.unit_code} - {prereq.prerequisite_unit?.unit_name}
                                        {prereq.minimum_grade && ` (Min Grade: ${prereq.minimum_grade})`}
                                      </li>
                                    ))}
                                  </ul>
                                </div>
                              )}

                              {/* Validation Status */}
                              {isSelected && validation && (
                                <div className={`mt-3 flex items-start gap-2 p-3 rounded-lg ${
                                  validation.type === 'success' ? 'bg-green-50' :
                                  validation.type === 'warning' ? 'bg-yellow-50' : 'bg-red-50'
                                }`}>
                                  <validation.icon className={`w-5 h-5 flex-shrink-0 ${
                                    validation.type === 'success' ? 'text-green-600' :
                                    validation.type === 'warning' ? 'text-yellow-600' : 'text-red-600'
                                  }`} />
                                  <div className="flex-1">
                                    <p className={`text-sm font-medium ${
                                      validation.type === 'success' ? 'text-green-800' :
                                      validation.type === 'warning' ? 'text-yellow-800' : 'text-red-800'
                                    }`}>
                                      {validation.message}
                                    </p>
                                    {validation.type === 'error' && validation.details && (
                                      <ul className="mt-1 text-xs text-red-700 list-disc list-inside">
                                        {validation.details.map((prereq, idx) => (
                                          <li key={idx}>
                                            Missing: {prereq.unit_name}
                                            {prereq.minimum_grade && ` (Required: ${prereq.minimum_grade}+)`}
                                          </li>
                                        ))}
                                      </ul>
                                    )}
                                  </div>
                                </div>
                              )}
                            </div>
                          </div>

                          {isEnrolled && (
                            <span className="ml-4 text-xs px-3 py-1 bg-green-100 text-green-800 rounded-full">
                              Already Enrolled
                            </span>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            </>
          )}
        </div>

        {/* Footer */}
        <div className="border-t bg-gray-50 px-6 py-4 flex items-center justify-between">
          <div className="text-sm text-gray-600">
            {selectedUnits.length} unit{selectedUnits.length !== 1 ? 's' : ''} selected
          </div>
          <div className="flex gap-3">
            <Button
              onClick={onClose}
              className="px-6 py-2 border border-gray-300 text-gray-700 hover:bg-gray-50 rounded-lg"
              disabled={loading}
            >
              Cancel
            </Button>
            <Button
              onClick={handleEnrollment}
              disabled={loading || selectedUnits.length === 0}
              className="px-6 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loading ? 'Processing...' : 'Enroll in Selected Units'}
            </Button>
            <Button
              onClick={handleCompleteRegistration}
              disabled={loading || currentEnrollments.filter(item => item.enrollment_status === 'enrolled').length === 0}
              className="px-6 py-2 bg-green-700 hover:bg-green-800 text-white rounded-lg disabled:opacity-50"
            >
              Complete &amp; bill registration
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default UnitRegistrationModal;
